import * as React from 'react'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { RenderErrorMessage } from '../error/RenderErrorMessage'
import { Pulse } from '../ui/Pulse'
import { createStateLens, type StateLens } from './react'
import { doNothing } from '../../../shared/lib/functional/function.no-deps'
import { ensureError } from '../../../shared/lib/functional/error.no-deps'

/**
 * PromiseState is meant to reify the current status of a promise in order
 * to be rendered in a react component
 */
export type PromiseState<T> = NotTriggered | InProgress | Resolved<T> | Rejected

export type NotTriggered = { status: 'not-triggered' }
export type InProgress = { status: 'in-progress'; cancel: () => void }
export type Resolved<T> = { status: 'resolved'; value: T }
export type Rejected = { status: 'rejected'; error: Error }

type Value<PS extends PromiseState<unknown>> = PS extends PromiseState<infer V> ? V : never

export const initialPromiseState = { status: 'not-triggered' as const }

/** @deprecated as badly typed */
export function promiseStateTrigger<S extends { [k in K]: PromiseState<Value<S[K]>> }, K extends keyof S>(
    deps: CommonDeps,
    component: React.Component<unknown, S>,
    key: K,
    fn: () => Promise<Value<S[K]>>
) {
    return safePromiseStateTrigger(deps, createStateLens(component, key), fn)
}

/**
 * Translate a promise to state.
 * Provides a cancel callback too by adding it to the loading state,
 * this is to avoid the react warning on unmounted components having their setState called.
 * This specific implementation was inspired by:
 * https://github.com/facebook/react/issues/5465#issuecomment-157888325
 */
export async function safePromiseStateTrigger<V>(
    deps: CommonDeps,
    lens: StateLens<PromiseState<V>>,
    fn: () => Promise<V>
): Promise<V> {
    promiseStateCancel(lens.get())

    let cancelled = false
    lens.set({ status: 'in-progress', cancel: () => (cancelled = true) })

    const valueP = fn()

    valueP
        .then((value) => {
            if (cancelled) return
            lens.set({ status: 'resolved', value })
        })
        .catch((error) => {
            if (cancelled) return
            error = ensureError(error)
            deps.reportError(error)
            lens.set({ status: 'rejected', error })
        })

    return valueP
}

/**
 * Use the cancel callback if the promise is still in the in-progress state,
 * otherwise just ignore it
 */
export function promiseStateCancel<T>(state: PromiseState<T>) {
    if (state.status === 'in-progress') {
        state.cancel()
    }
}

export function promiseStateReset<T>(lens: StateLens<PromiseState<T>>) {
    promiseStateCancel(lens.get())
    lens.set(initialPromiseState)
}

export interface PromiseStateRenderOptions {
    notTriggered?: () => React.ReactElement<unknown>
    inProgress?: () => React.ReactElement<unknown>
    rejected?: (error: Error | string) => React.ReactElement<unknown>
}

/**
 * Give a default rendering for the different states that seem to make sense
 * Can override a specific case in consumer simply by handling the specific case before calling this
 */
export function promiseStateRender<T>(
    deps: CommonDeps,
    state: PromiseState<T>,
    render: (value: T) => React.ReactElement<unknown>,
    opts?: PromiseStateRenderOptions
) {
    return renderCatch(deps, () => {
        switch (state.status) {
            case 'not-triggered':
                return opts?.notTriggered ? opts.notTriggered() : null
            case 'in-progress':
                return opts?.inProgress ? opts.inProgress() : <Pulse />
            case 'rejected':
                return opts?.rejected ? (
                    opts.rejected(state.error)
                ) : (
                    <RenderErrorMessage
                        getImage={deps.getImage}
                        language={deps.language}
                        triggerSentryReplay={deps.triggerSentryReplay}
                    />
                )
            case 'resolved':
                return render(state.value)
            default:
                return null
        }
    })
}

/**
 * Will return the value of the promise if available,
 * otherwise it will be null
 */
export function promiseStateMaybeGet<T>(state: PromiseState<T>): T | null {
    return state.status === 'resolved' ? state.value : null
}

export function promiseStateMustGet<T>(state: PromiseState<T>): T {
    if (state.status !== 'resolved') throw new Error('Attempting to access state of an unresolved promise')
    return state.value
}

export function promiseStateMaybeGetError<T>(state: PromiseState<T>): Error | null {
    return state.status === 'rejected' ? state.error : null
}

export function promiseStateMustGetError<T>(state: PromiseState<T>): Error {
    if (state.status !== 'rejected') throw new Error('Attempting to access error of an unrejected promise')
    return state.error
}

/**
 * Used to update the resolved promise in place when the user interacts with the fetched data.
 * If the promiseState is not resolved, than do not update
 */
export function promiseStateUpdate<T>(state: PromiseState<T>, updater: (value: T) => T): PromiseState<T> {
    if (state.status !== 'resolved') return state
    return { status: 'resolved', value: updater(state.value) }
}

export type DepromiseStateAll<PromiseStates> = PromiseStates extends [PromiseState<infer T1>, ...infer Rest]
    ? [T1, ...DepromiseStateAll<Rest>]
    : []

export function promiseStateAll<PromiseStates extends Array<PromiseState<unknown>>>(
    ...states: PromiseStates
): PromiseState<DepromiseStateAll<PromiseStates>> {
    const error = states.find(isPromiseStateRejected)
    if (error) return { status: 'rejected', error: error.error }

    const inProgress = states.find(isPromiseStateInProgress)
    if (inProgress) return { status: 'in-progress', cancel: doNothing }

    const notTriggered = states.find(isPromiseStateNotTriggered)
    if (notTriggered) return { status: 'not-triggered' }

    return { status: 'resolved', value: states.map((state: any) => state.value) as any }
}

export function promiseStateRenderAll<PromiseStates extends Array<PromiseState<unknown>>>(
    deps: CommonDeps,
    states: PromiseStates,
    render: (values: DepromiseStateAll<PromiseStates>) => React.ReactElement<unknown>,
    opts?: PromiseStateRenderOptions
): React.ReactElement<unknown> {
    const allState = promiseStateAll(...states)
    return promiseStateRender(deps, allState, render, opts)
}

export function isPromiseStateNotTriggered<T>(state: PromiseState<T>): state is NotTriggered {
    return state.status === 'not-triggered'
}

export function isPromiseStateInProgress<T>(state: PromiseState<T>): state is InProgress {
    return state.status === 'in-progress'
}

export function isPromiseStateResolved<T>(state: PromiseState<T>): state is Resolved<T> {
    return state.status === 'resolved'
}

export function isPromiseStateRejected<T>(state: PromiseState<T>): state is Rejected {
    return state.status === 'rejected'
}
